import { FC } from 'react';

const AIAutomationPage: FC = () => {
  return (
    <div className="ai-automation-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 py-16">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-center">AI & Automation Solutions</h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            Transforming financial service marketing through cutting-edge artificial intelligence and automation
          </p>
        </div>
      </section>

      {/* AI Overview Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="text-3xl font-bold mb-6">Revolutionizing Financial Marketing</h2>
              <p className="mb-4">
                At DLDV Enterprises, we leverage the latest advancements in artificial intelligence and automation to transform how financial service providers approach marketing and business development.
              </p>
              <p className="mb-4">
                Our AI-powered solutions streamline processes, enhance decision-making, and deliver personalized experiences at scale – all while maintaining the human touch that is essential in financial services.
              </p>
              <p>
                By combining deep industry expertise with cutting-edge technology, we help investment firms and financial service providers achieve unprecedented efficiency and effectiveness in their marketing efforts.
              </p>
            </div>
            <div className="flex justify-center">
              <img src="/images/dldv.svg" alt="DLDV Enterprises" className="w-full max-w-md" />
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our AI Capabilities</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Predictive Analytics</h3>
              <p>
                Leverage machine learning algorithms to identify patterns, predict investor behavior, and optimize marketing strategies based on data-driven insights.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Automated Content Generation</h3>
              <p>
                Create personalized, compliance-approved marketing materials, pitch decks, and investment memoranda with AI-powered content generation tools.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Intelligent Workflow Automation</h3>
              <p>
                Streamline marketing operations with automated workflows that reduce manual tasks, minimize errors, and accelerate time-to-market.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Investor Matching</h3>
              <p>
                Match investment opportunities with the right investors using AI algorithms that analyze preferences, past behavior, and investment criteria.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Multilingual Communication</h3>
              <p>
                Break down language barriers with AI-powered translation and localization tools that enable effective communication with global investors.
              </p>
            </div>
            
            <div className="card">
              <div className="text-accent-color mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3">Compliance Monitoring</h3>
              <p>
                Ensure marketing materials adhere to regulatory requirements with AI-powered compliance monitoring and risk assessment tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Implementation Process</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <div className="relative pl-10 mb-8">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">1</div>
                <h3 className="text-xl font-bold mb-2">Assessment & Strategy</h3>
                <p>
                  We begin with a thorough assessment of your current marketing processes, challenges, and objectives to develop a customized AI implementation strategy.
                </p>
              </div>
              
              <div className="relative pl-10 mb-8">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">2</div>
                <h3 className="text-xl font-bold mb-2">Solution Design</h3>
                <p>
                  Our team designs AI solutions tailored to your specific needs, integrating with your existing systems and workflows for seamless adoption.
                </p>
              </div>
              
              <div className="relative pl-10">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">3</div>
                <h3 className="text-xl font-bold mb-2">Implementation & Training</h3>
                <p>
                  We implement the solutions and provide comprehensive training to ensure your team can effectively leverage the new AI capabilities.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative pl-10 mb-8">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">4</div>
                <h3 className="text-xl font-bold mb-2">Monitoring & Optimization</h3>
                <p>
                  We continuously monitor the performance of your AI solutions, making data-driven adjustments to optimize results over time.
                </p>
              </div>
              
              <div className="relative pl-10 mb-8">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">5</div>
                <h3 className="text-xl font-bold mb-2">Scaling & Expansion</h3>
                <p>
                  As your needs evolve, we help you scale and expand your AI capabilities to address new challenges and opportunities.
                </p>
              </div>
              
              <div className="relative pl-10">
                <div className="absolute left-0 top-0 bg-accent-color text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">6</div>
                <h3 className="text-xl font-bold mb-2">Ongoing Support</h3>
                <p>
                  Our team provides ongoing support and guidance to ensure your AI solutions continue to deliver value as technology and market conditions change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Benefits of AI-Enhanced Marketing</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Increased Efficiency</h3>
              <p>
                Automate routine tasks and streamline workflows to reduce manual effort and accelerate marketing processes by up to 70%.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Enhanced Personalization</h3>
              <p>
                Deliver highly personalized marketing communications and investor experiences at scale, improving engagement and conversion rates.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Data-Driven Decisions</h3>
              <p>
                Make informed marketing decisions based on AI-powered analytics and insights, optimizing resource allocation and strategy.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Competitive Advantage</h3>
              <p>
                Stay ahead of the competition by leveraging cutting-edge AI technologies that differentiate your firm in the marketplace.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Regulatory Compliance</h3>
              <p>
                Ensure marketing materials and communications adhere to regulatory requirements with AI-powered compliance monitoring.
              </p>
            </div>
            
            <div className="card">
              <h3 className="text-xl font-bold mb-3">Scalable Operations</h3>
              <p>
                Scale your marketing efforts without proportionally increasing headcount or costs, enabling sustainable growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Marketing with AI?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contact us today to discuss how our AI and automation solutions can help your financial service firm achieve exceptional results.
          </p>
          <a href="https://calendly.com/dirk-de-vlaam/meeting-with-dirk-de-vlaam-from-dldv-enterprises" target="_blank" rel="noopener noreferrer" className="btn-primary">Schedule a Consultation</a>
        </div>
      </section>
    </div>
  );
};

export default AIAutomationPage;
